// Excel import/export for route points · supports 3 variants (concise/story/talky).
// 用 exceljs 而不是 SheetJS：需要给单元格设置宋体字体（用户硬性要求），
// 社区版 SheetJS 不支持样式写入。exceljs ~700KB，所以都走动态 import，
// 不影响初始 bundle 大小。
//
// 工作簿结构：
//   每个方向（上行/下行）= 一个 .xlsx 文件，文件内固定 3 个 sheet：
//     [简约版] · [故事版] · [话痨版]
//   每个 sheet 的列结构与单 variant 一致（15 列）：
//     前 5 列点位基础信息（seq/name/lat/lng/triggerRadius）+ 5 段 ×（讲解词 / 间隔秒）
//   3 个 sheet 的点位 seq / name 必须对齐 —— 导入时按 seq 做合并。
//   即使某段没内容，也保留位置（空着即可），方便编辑时一目了然。

export type SegmentPart = "segment_1" | "segment_2" | "question" | "answer_yes" | "answer_no";
export type SegmentVariant = "concise" | "story" | "talky";

export interface SegmentCell {
  script: string;
  pauseSec: number;
}

// 单 variant 下的一个点位 5 段
export interface PointVariantCells {
  segment1: SegmentCell;
  segment2: SegmentCell;
  question: SegmentCell;
  answerYes: SegmentCell;
  answerNo: SegmentCell;
}

export interface PointRow {
  seq: number;
  name: string;
  lat: number;
  lng: number;
  triggerRadius: number;
  triggerMode?: "circle" | "strip";
  triggerHeadingDeg?: number | null;
  triggerWidthM?: number | null;
  // 3 套讲解风格 —— 每段都可独立录 3 套；缺失的 variant 保留空槽位
  variants: Record<SegmentVariant, PointVariantCells>;
}

export const SEGMENT_PARTS: SegmentPart[] = [
  "segment_1",
  "segment_2",
  "question",
  "answer_yes",
  "answer_no",
];

export const SEGMENT_LABELS: Record<SegmentPart, string> = {
  segment_1: "第1段·引入",
  segment_2: "第2段·主讲解",
  question: "第3段·互动问题",
  answer_yes: "第4段·肯定回答",
  answer_no: "第4段·否定回答",
};

export const VARIANT_LABELS: Record<SegmentVariant, string> = {
  concise: "简约版",
  story: "故事版",
  talky: "话痨版",
};

// 导出/模板的 sheet 顺序：简约 → 故事 → 话痨。
export const VARIANT_ORDER: SegmentVariant[] = ["concise", "story", "talky"];

// 根据 sheet name 反推 variant；用户改名或者只导入部分 variant 也兼容。
export function variantFromSheetName(name: string): SegmentVariant | null {
  const trimmed = (name || "").trim();
  if (!trimmed) return null;
  if (trimmed.includes("简约")) return "concise";
  if (trimmed.includes("故事")) return "story";
  if (trimmed.includes("话痨")) return "talky";
  // 兼容直接用 variant key 命名的 sheet
  if (trimmed === "concise" || trimmed === "story" || trimmed === "talky") {
    return trimmed as SegmentVariant;
  }
  return null;
}

const HEADERS = [
  "顺序",
  "点位名称",
  "纬度",
  "经度",
  "触发半径(米)",
  "触发方式",
  "航向角(度)",
  "容忍宽度(米)",
  "第1段·引入",
  "第1段·间隔(秒)",
  "第2段·主讲解",
  "第2段·间隔(秒)",
  "第3段·互动问题",
  "第3段·间隔(秒)",
  "第4段·肯定回答",
  "第4段·肯定间隔(秒)",
  "第4段·否定回答",
  "第4段·否定间隔(秒)",
] as const;

const COL_KEYS = [
  "seq",
  "name",
  "lat",
  "lng",
  "triggerRadius",
  "triggerMode",
  "triggerHeadingDeg",
  "triggerWidthM",
  "seg1Script",
  "seg1Pause",
  "seg2Script",
  "seg2Pause",
  "qScript",
  "qPause",
  "ayScript",
  "ayPause",
  "anScript",
  "anPause",
] as const;

const COL_WIDTHS = [6, 22, 11, 11, 12, 12, 12, 18, 40, 12, 50, 12, 40, 12, 40, 14, 40, 14];

// 内容列（讲解词文本，靠左换行），1-based
const TEXT_COL_INDICES = new Set([2, 9, 11, 13, 15, 17]);

const FONT_BODY = { name: "宋体", size: 11 } as const;
const FONT_HEAD = { name: "宋体", size: 11, bold: true, color: { argb: "FF1F2937" } } as const;

function emptyCell(): SegmentCell {
  return { script: "", pauseSec: 0 };
}

function emptyVariantCells(): PointVariantCells {
  return {
    segment1: emptyCell(),
    segment2: emptyCell(),
    question: emptyCell(),
    answerYes: emptyCell(),
    answerNo: emptyCell(),
  };
}

function emptyRow(
  seq: number,
  name = "",
  lat = 0,
  lng = 0,
  triggerRadius = 300,
  triggerMode?: "circle" | "strip",
  triggerHeadingDeg?: number | null,
  triggerWidthM?: number | null
): PointRow {
  return {
    seq,
    name,
    lat,
    lng,
    triggerRadius,
    triggerMode,
    triggerHeadingDeg,
    triggerWidthM,
    variants: {
      concise: emptyVariantCells(),
      story: emptyVariantCells(),
      talky: emptyVariantCells(),
    },
  };
}

function variantCellsToObject(r: PointRow, v: SegmentVariant) {
  const cells = r.variants[v];
  return {
    seq: r.seq,
    name: r.name,
    lat: r.lat,
    lng: r.lng,
    triggerRadius: r.triggerRadius,
    triggerMode: r.triggerMode === "strip" ? "条带" : "圆形",
    triggerHeadingDeg: r.triggerHeadingDeg !== null && r.triggerHeadingDeg !== undefined ? r.triggerHeadingDeg : "",
    triggerWidthM: r.triggerWidthM !== null && r.triggerWidthM !== undefined ? r.triggerWidthM : "",
    seg1Script: cells.segment1.script,
    seg1Pause: cells.segment1.pauseSec,
    seg2Script: cells.segment2.script,
    seg2Pause: cells.segment2.pauseSec,
    qScript: cells.question.script,
    qPause: cells.question.pauseSec,
    ayScript: cells.answerYes.script,
    ayPause: cells.answerYes.pauseSec,
    anScript: cells.answerNo.script,
    anPause: cells.answerNo.pauseSec,
  };
}

async function loadExcelJS() {
  // 动态 import 让 exceljs 进入独立 chunk
  const mod = await import("exceljs");
  return (mod as unknown as { default?: typeof import("exceljs") }).default ?? mod;
}

type ExcelJSModule = Awaited<ReturnType<typeof loadExcelJS>>;
type Workbook = InstanceType<ExcelJSModule["Workbook"]>;
type Worksheet = ReturnType<Workbook["addWorksheet"]>;

function applyStyles(ws: Worksheet) {
  ws.columns = HEADERS.map((h, i) => ({
    header: h,
    key: COL_KEYS[i],
    width: COL_WIDTHS[i],
  }));
  const headerRow = ws.getRow(1);
  headerRow.height = 28;
  headerRow.eachCell((c) => {
    c.font = FONT_HEAD;
    c.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE6F4F1" } };
    c.border = {
      bottom: { style: "thin", color: { argb: "FF99C9BE" } },
    };
  });
}

function styleDataRows(ws: Worksheet) {
  ws.eachRow({ includeEmpty: false }, (row, idx) => {
    if (idx === 1) return;
    row.eachCell({ includeEmpty: true }, (c, col) => {
      c.font = FONT_BODY;
      const isText = TEXT_COL_INDICES.has(col);
      c.alignment = {
        vertical: "top",
        wrapText: true,
        horizontal: isText ? "left" : "center",
      };
    });
  });
}

async function downloadWorkbook(wb: Workbook, filename: string) {
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    a.remove();
    URL.revokeObjectURL(a.href);
  }, 0);
}

// 给一个 sheet 填上某 variant 的全部点位行 + 样式
function fillVariantSheet(ws: Worksheet, rows: PointRow[], variant: SegmentVariant) {
  applyStyles(ws);
  for (const r of rows) {
    ws.addRow(variantCellsToObject(r, variant));
  }
  styleDataRows(ws);
  ws.views = [{ state: "frozen", ySplit: 1, xSplit: 2 }];
}

export async function exportPointsToExcel(
  routeName: string,
  direction: "up" | "down",
  rows: PointRow[],
) {
  const ExcelJS = await loadExcelJS();
  const wb = new ExcelJS.Workbook();
  wb.creator = "三峡 AI 导览";
  wb.created = new Date();
  // 3 个 sheet：简约版 / 故事版 / 话痨版（顺序固定）
  for (const v of VARIANT_ORDER) {
    const ws = wb.addWorksheet(VARIANT_LABELS[v]);
    fillVariantSheet(ws, rows, v);
  }
  const dirLabel = direction === "up" ? "上行" : "下行";
  const safe = (s: string) => s.replace(/[\\/:*?"<>|]/g, "_");
  await downloadWorkbook(wb, `${safe(routeName)}-${dirLabel}-点位(3套讲解风格).xlsx`);
}

export async function downloadTemplate() {
  const ExcelJS = await loadExcelJS();
  const wb = new ExcelJS.Workbook();
  wb.creator = "三峡 AI 导览";
  wb.created = new Date();

  // 同一组示例点位，3 套 variant 各对应一个 sheet，方便用户直接对比改写。
  const baseRows: PointRow[] = [
    {
      ...emptyRow(1, "示例点位 · 三峡大坝", 30.8235, 111.0033, 300, "circle", null, null),
      variants: {
        concise: {
          ...emptyVariantCells(),
          segment2: { script: "（简约版）一句话点睛：长江上的水电奇迹，亚洲最大双线五级船闸。", pauseSec: 2 },
        },
        story: {
          ...emptyVariantCells(),
          segment1: { script: "（故事版-引入）各位船友请看船首方向…", pauseSec: 2 },
          segment2: { script: "（故事版-主讲解）三峡大坝 1994 年开工，2009 年全部完工。这里有…", pauseSec: 3 },
          question: { script: "（故事版-互动问题）您知道大坝五级船闸提升了多少米吗？", pauseSec: 4 },
          answerYes: { script: "（故事版-肯定回答）没错，113 米，相当于 38 层楼…", pauseSec: 2 },
          answerNo: { script: "（故事版-否定回答）其实是 113 米，相当于 38 层楼…", pauseSec: 2 },
        },
        talky: {
          ...emptyVariantCells(),
          segment2: { script: "（话痨版-主讲解）让我多讲几句：三峡大坝不仅是发电站，还兼防洪、通航、补水。你猜全年发电量？相当于多少个核电站…", pauseSec: 3 },
        },
      },
    },
    {
      ...emptyRow(2, "示例点位 · 葛洲坝", 30.7378, 111.271, 250, "strip", 90, 100),
      variants: {
        concise: {
          ...emptyVariantCells(),
          segment2: { script: "（简约版）葛洲坝：长江第一坝。", pauseSec: 2 },
        },
        story: {
          ...emptyVariantCells(),
          segment2: { script: "（故事版）葛洲坝是长江干流上的第一座大型水电站…", pauseSec: 3 },
        },
        talky: emptyVariantCells(),
      },
    },
  ];

  for (const v of VARIANT_ORDER) {
    const ws = wb.addWorksheet(VARIANT_LABELS[v]);
    fillVariantSheet(ws, baseRows, v);
    // 在数据下方加一行使用说明（淡色提示）
    const help = ws.addRow({
      seq: "",
      name: "↑ 上方为示例行，使用前请删除",
      lat: "",
      lng: "",
      triggerRadius: "",
      seg1Script: "顺序：按数字升序追加到当前方向末尾；触发半径单位米；",
      seg1Pause: "",
      seg2Script: "5 段固定槽位即使没内容也保留位置（留空即可）；",
      seg2Pause: "",
      qScript: "3 个 sheet（简约 / 故事 / 话痨）按相同顺序导入；",
      qPause: "",
      ayScript: "导入后只覆盖有内容的段，空段不会清除原有内容；",
      ayPause: "",
      anScript: "可以只填一个 sheet，其它两套留空。",
      anPause: "",
    });
    help.eachCell((c) => {
      c.font = { ...FONT_BODY, italic: true, color: { argb: "FF999999" } };
      c.alignment = { vertical: "top", wrapText: true };
    });
  }
  await downloadWorkbook(wb, "点位导入模板(3套讲解风格).xlsx");
}

// 把一个 sheet 解析成 (seq → PointVariantCells) map
type SheetParseEntry = {
  seq: number;
  name: string;
  lat: number;
  lng: number;
  triggerRadius: number;
  triggerMode?: "circle" | "strip";
  triggerHeadingDeg?: number | null;
  triggerWidthM?: number | null;
  cells: PointVariantCells;
};

function parseSheet(ws: Worksheet): SheetParseEntry[] {
  const cellText = (v: unknown): string => {
    if (v == null) return "";
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
    if (v instanceof Date) return v.toISOString();
    if (typeof v === "object") {
      const obj = v as { text?: unknown; result?: unknown; richText?: { text?: string }[] };
      if (Array.isArray(obj.richText)) {
        return obj.richText.map((rt) => rt.text ?? "").join("");
      }
      if (obj.text != null) return String(obj.text);
      if (obj.result != null) return String(obj.result);
    }
    return String(v);
  };

  const num = (v: unknown, fallback = 0) => {
    const n = Number(cellText(v));
    return Number.isFinite(n) ? n : fallback;
  };

  const numOrNull = (v: unknown): number | null => {
    if (v == null || cellText(v).trim() === "") return null;
    const n = Number(cellText(v));
    return Number.isFinite(n) ? n : null;
  };

  // Check if it's the new template format (has 触发方式/触发模式 column)
  let hasTriggerModeCol = false;
  let triggerModeColIdx = -1;
  let triggerHeadingColIdx = -1;
  let triggerWidthColIdx = -1;

  const firstRow = ws.getRow(1);
  for (let i = 1; i <= Math.max(ws.columnCount || 0, 20); i++) {
    const val = cellText(firstRow.getCell(i).value).trim();
    if (val.includes("触发方式") || val.includes("触发模式")) {
      hasTriggerModeCol = true;
      triggerModeColIdx = i;
    } else if (val.includes("航向角") || val.includes("朝向角")) {
      triggerHeadingColIdx = i;
    } else if (val.includes("容忍宽度") || val.includes("宽度/左右容差")) {
      triggerWidthColIdx = i;
    }
  }

  const out: SheetParseEntry[] = [];
  ws.eachRow({ includeEmpty: false }, (row, idx) => {
    if (idx === 1) return; // skip header
    const seqRaw = cellText(row.getCell(1).value).trim();
    const name = cellText(row.getCell(2).value).trim();
    // 跳过示例提示行和空行
    if (!name || name.startsWith("↑")) return;

    let triggerMode: "circle" | "strip" = "circle";
    let triggerHeadingDeg: number | null = null;
    let triggerWidthM: number | null = null;

    let s1Idx = 6, s1pIdx = 7;
    let s2Idx = 8, s2pIdx = 9;
    let qIdx = 10, qpIdx = 11;
    let ayIdx = 12, aypIdx = 13;
    let anIdx = 14, anpIdx = 15;

    if (hasTriggerModeCol) {
      const modeRaw = cellText(row.getCell(triggerModeColIdx).value).trim().toLowerCase();
      if (modeRaw === "strip" || modeRaw === "条带") {
        triggerMode = "strip";
      } else {
        triggerMode = "circle";
      }

      if (triggerHeadingColIdx !== -1) {
        triggerHeadingDeg = numOrNull(row.getCell(triggerHeadingColIdx).value);
      }
      if (triggerWidthColIdx !== -1) {
        triggerWidthM = numOrNull(row.getCell(triggerWidthColIdx).value);
        if (triggerWidthM !== null) {
          triggerWidthM = Math.max(0, Math.round(triggerWidthM));
        }
      }

      if (triggerMode === "strip") {
        const isHeadingValid = triggerHeadingDeg !== null && Number.isFinite(triggerHeadingDeg);
        const isWidthValid = triggerWidthM !== null && Number.isFinite(triggerWidthM) && triggerWidthM > 0;
        if (!isHeadingValid || !isWidthValid) {
          triggerMode = "circle";
          triggerHeadingDeg = null;
          triggerWidthM = null;
        }
      } else {
        triggerHeadingDeg = null;
        triggerWidthM = null;
      }

      const baseIdx = Math.max(triggerModeColIdx, triggerHeadingColIdx, triggerWidthColIdx);
      s1Idx = baseIdx + 1;
      s1pIdx = baseIdx + 2;
      s2Idx = baseIdx + 3;
      s2pIdx = baseIdx + 4;
      qIdx = baseIdx + 5;
      qpIdx = baseIdx + 6;
      ayIdx = baseIdx + 7;
      aypIdx = baseIdx + 8;
      anIdx = baseIdx + 9;
      anpIdx = baseIdx + 10;
    }

    out.push({
      seq: Number(seqRaw) || idx - 1,
      name,
      lat: num(row.getCell(3).value),
      lng: num(row.getCell(4).value),
      triggerRadius: Math.max(0, Math.round(num(row.getCell(5).value))),
      triggerMode,
      triggerHeadingDeg,
      triggerWidthM,
      cells: {
        segment1: {
          script: cellText(row.getCell(s1Idx).value).trim(),
          pauseSec: Math.max(0, Math.round(num(row.getCell(s1pIdx).value))),
        },
        segment2: {
          script: cellText(row.getCell(s2Idx).value).trim(),
          pauseSec: Math.max(0, Math.round(num(row.getCell(s2pIdx).value))),
        },
        question: {
          script: cellText(row.getCell(qIdx).value).trim(),
          pauseSec: Math.max(0, Math.round(num(row.getCell(qpIdx).value))),
        },
        answerYes: {
          script: cellText(row.getCell(ayIdx).value).trim(),
          pauseSec: Math.max(0, Math.round(num(row.getCell(aypIdx).value))),
        },
        answerNo: {
          script: cellText(row.getCell(anIdx).value).trim(),
          pauseSec: Math.max(0, Math.round(num(row.getCell(anpIdx).value))),
        },
      },
    });
  });
  return out;
}

export async function parseExcelFile(file: File): Promise<PointRow[]> {
  const ExcelJS = await loadExcelJS();
  const buf = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  if (wb.worksheets.length === 0) return [];

  // 第一遍：识别哪些 sheet 对应 variant；剩下未识别的 sheet 当作"故事版"处理（向后兼容旧模板）。
  const sheetVariants = new Map<Worksheet, SegmentVariant>();
  let assignedConcise = false;
  for (const ws of wb.worksheets) {
    const v = variantFromSheetName(ws.name);
    if (v) {
      sheetVariants.set(ws, v);
      if (v === "concise") assignedConcise = true;
    }
  }
  // 旧模板只有一个 sheet（"上行"/"下行"），按 concise 处理 —— 与生产历史数据归类一致
  if (sheetVariants.size === 0) {
    sheetVariants.set(wb.worksheets[0], "concise");
    assignedConcise = true;
  } else if (!assignedConcise) {
    // 用户漏了"简约版"sheet —— 把第一个 sheet 当作 concise，避免主版本完全没数据
    sheetVariants.set(wb.worksheets[0], "concise");
  }

  // 第二遍：以"简约版"sheet 的 seq 序列作为点位锚点（最完整、最常有内容的一套），
  // 其它 variant sheet 按 seq 对齐合并（找不到对应 seq 的行自动忽略）。
  const variantEntries = new Map<SegmentVariant, SheetParseEntry[]>();
  for (const [ws, v] of sheetVariants) {
    variantEntries.set(v, parseSheet(ws));
  }
  const anchorEntries = variantEntries.get("concise") ?? variantEntries.get("story") ?? variantEntries.get("talky") ?? [];
  if (anchorEntries.length === 0) return [];

  // 锚点按 seq 升序
  anchorEntries.sort((a, b) => a.seq - b.seq);

  const rows: PointRow[] = anchorEntries.map((anchor) => {
    const variants: Record<SegmentVariant, PointVariantCells> = {
      concise: emptyVariantCells(),
      story: emptyVariantCells(),
      talky: emptyVariantCells(),
    };
    for (const v of VARIANT_ORDER) {
      const list = variantEntries.get(v);
      if (!list) continue;
      const hit = list.find((e) => e.seq === anchor.seq) ?? list.find((e) => e.name === anchor.name);
      if (hit) variants[v] = hit.cells;
    }
    return {
      seq: anchor.seq,
      name: anchor.name,
      lat: anchor.lat,
      lng: anchor.lng,
      triggerRadius: anchor.triggerRadius,
      triggerMode: anchor.triggerMode,
      triggerHeadingDeg: anchor.triggerHeadingDeg,
      triggerWidthM: anchor.triggerWidthM,
      variants,
    };
  });
  return rows;
}
